import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/shared/PageHeader";
import { RefreshingIndicator } from "@/components/shared/RefreshingIndicator";
import {
  SMS_TRANSACTIONAL_CONSENT_REQUIRED_MESSAGE,
  SmsConsentConfirmation,
} from "@/components/shared/SmsConsentConfirmation";
import { StatusChip } from "@/components/shared/StatusChip";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TableSkeleton } from "@/components/shared/TableSkeleton";
import { StateCard } from "@/components/shared/StateCard";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { apiRequest } from "@/lib/api";
import { sendApprovalReminder } from "@/lib/approvals";
import { invalidateTicketData } from "@/lib/query-invalidation";
import {
  API_TICKET_STATUSES,
  ApiTicketStatus,
  ApiTicketType,
  APPROVAL_QUEUE_STATUSES,
  formatCurrency,
  mapApiStatusToUi,
  mapApiTypeToLabel,
} from "@/lib/tickets";
import { AlertTriangle, ArrowUpDown, Bell, ChevronDown, ChevronUp, FileText, FileX, Info, Loader2, MoreHorizontal, Pencil, Plus, RefreshCw, Search, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/auth/auth-context";
import { queryKeys, type TicketListItem, useCompanyProfileQuery, useCompanySettingsQuery, useTicketsQuery } from "@/hooks/use-app-queries";

type ManualActionState = {
  ticket: TicketListItem;
  decision: "APPROVED" | "REJECTED";
};

type TicketSortKey = "createdAt" | "status" | "project";
type TicketSortDirection = "asc" | "desc";

const STATUS_SORT_ORDER: Record<ApiTicketStatus, number> = {
  APPROVED: 0,
  PENDING_APPROVAL: 1,
  NEEDS_CLARIFICATION: 2,
  REJECTED: 3,
  DRAFT: 4,
  ARCHIVED: 5,
};

function formatCompactAgingFromDate(date: string) {
  const diffMs = Math.max(0, Date.now() - new Date(date).getTime());
  const totalMinutes = Math.floor(diffMs / (1000 * 60));
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const totalHours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (totalHours < 24) {
    return minutes > 0 ? `${totalHours}h ${minutes}m` : `${totalHours}h`;
  }
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
}

function formatTicketCreatedAt(date: string) {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(date));
}

function formatTicketOpenedAt(date: string) {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(date));
}

function renderOpenedIndicator(
  ticket: Pick<TicketListItem, "lastOpenedAt" | "status">,
  t: (key: string) => string,
) {
  if (ticket.lastOpenedAt) {
    return formatTicketOpenedAt(ticket.lastOpenedAt);
  }

  if (ticket.status === "APPROVED") {
    return (
      <span className="inline-flex rounded-full bg-status-approved-bg px-2 py-1 text-xs font-semibold text-status-approved">
        {t("tickets.onsiteApproved")}
      </span>
    );
  }

  return (
    <span className="inline-flex rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-700">
      {t("tickets.unopened")}
    </span>
  );
}

function shouldShowAging(status: ApiTicketStatus) {
  return APPROVAL_QUEUE_STATUSES.includes(status) || status === "REJECTED";
}

function assignedMemberLabel(ticket: TicketListItem) {
  return (
    ticket.project.assignedMember?.fullName?.trim() ||
    ticket.project.assignedMember?.email?.trim() ||
    ""
  );
}

function CompactTicketCard({
  ticket,
  actions,
  onOpen,
}: {
  ticket: TicketListItem;
  actions: ReactNode;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const hasTeamAssignment = user?.currentPlan === "TEAM";
  const typeLabel =
    ticket.type === "TM_TICKET"
      ? t("ticketTypes.estimate")
      : ticket.type === "CHANGE_ORDER"
        ? t("ticketTypes.changeOrder")
        : t("ticketTypes.extraWork");
  return (
    <div
      className="rounded-lg border bg-card p-4 cursor-pointer hover:bg-muted/20 transition-colors"
      onClick={onOpen}
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
            <p className="truncate text-sm font-semibold text-accent" title={typeLabel}>
              {typeLabel}
            </p>
          </div>
          <p className="mt-1 truncate text-xs text-muted-foreground" title={ticket.project.name}>
            {ticket.project.name}
          </p>
          {hasTeamAssignment ? (
            assignedMemberLabel(ticket) ? (
              <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
                Assigned to: {assignedMemberLabel(ticket)}
              </p>
            ) : (
              <span className="mt-1 inline-flex w-fit rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                Unassigned
              </span>
            )
          ) : null}
          <p className="mt-0.5 truncate text-xs text-muted-foreground" title={ticket.client?.name ?? t("tickets.noClient")}>
            {ticket.client?.name ?? t("tickets.noClient")}
          </p>
          <p className="mt-1 text-[11px] text-muted-foreground">
            {t("tickets.createdAt")}: {formatTicketCreatedAt(ticket.createdAt)}
          </p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {t("tickets.opened")}: {renderOpenedIndicator(ticket, t)}
          </p>
        </div>
        <div className="flex items-start gap-2 shrink-0">
          <div className="flex flex-col items-end gap-2">
            <StatusChip status={mapApiStatusToUi(ticket.status)} />
            <p className="text-sm font-semibold text-accent">{formatCurrency(ticket.totalAmount)}</p>
          </div>
          <div className="self-stretch" onClick={(event) => event.stopPropagation()}>{actions}</div>
        </div>
      </div>
    </div>
  );
}

export default function Tickets() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  const hasTeamAssignment = user?.currentPlan === "TEAM";
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("search") ?? "");
  const [ticketHelpOpen, setTicketHelpOpen] = useState(false);
  const initialStatus = searchParams.get("status");
  const initialType = searchParams.get("type");
  const [status, setStatus] = useState<"ALL" | ApiTicketStatus>(
    initialStatus &&
      API_TICKET_STATUSES.includes(initialStatus as ApiTicketStatus)
      ? (initialStatus as ApiTicketStatus)
      : "ALL",
  );
  const [type, setType] = useState<"ALL" | ApiTicketType>(
    initialType === "TM_TICKET" || initialType === "CHANGE_ORDER" || initialType === "EXTRA_WORK"
      ? initialType
      : "ALL",
  );
  const [deleteTarget, setDeleteTarget] = useState<TicketListItem | null>(null);
  const [deleteImpact, setDeleteImpact] = useState<{
    canDeleteProject: boolean;
    canDeleteClient: boolean;
    project: { id: string; name: string } | null;
    client: { id: string; name: string } | null;
  } | null>(null);
  const [deleteProjectWithTicket, setDeleteProjectWithTicket] = useState(false);
  const [deleteClientWithTicket, setDeleteClientWithTicket] = useState(false);
  const [deleteImpactLoading, setDeleteImpactLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [allowManualOverride, setAllowManualOverride] = useState(false);
  const [requireRejectionComment, setRequireRejectionComment] = useState(true);
  const [manualAction, setManualAction] = useState<ManualActionState | null>(null);
  const [manualReason, setManualReason] = useState("");
  const [processingManualAction, setProcessingManualAction] = useState(false);
  const [sendingReminderTicketId, setSendingReminderTicketId] = useState<string | null>(null);
  const [smsReminderTarget, setSmsReminderTarget] = useState<TicketListItem | null>(null);
  const [smsReminderConsentConfirmed, setSmsReminderConsentConfirmed] = useState(false);
  const [smsReminderConsentError, setSmsReminderConsentError] = useState("");
  const [sortBy, setSortBy] = useState<TicketSortKey>("createdAt");
  const [sortDirection, setSortDirection] = useState<TicketSortDirection>("desc");

  const isAdmin = user?.role === "ADMIN";
  const currentTicketQueryKey = useMemo(
    () =>
      queryKeys.tickets({
        search: search.trim(),
        status,
        type,
        projectId: searchParams.get("projectId") ?? "",
        startDate: searchParams.get("startDate") ?? "",
        endDate: searchParams.get("endDate") ?? "",
      }),
    [search, searchParams, status, type],
  );
  const ticketsQuery = useTicketsQuery({
    search,
    status,
    type,
    projectId: searchParams.get("projectId") ?? "",
    startDate: searchParams.get("startDate") ?? "",
    endDate: searchParams.get("endDate") ?? "",
  });
  const allStatusTicketsQuery = useTicketsQuery({
    search,
    status: "ALL",
    type,
    projectId: searchParams.get("projectId") ?? "",
    startDate: searchParams.get("startDate") ?? "",
    endDate: searchParams.get("endDate") ?? "",
  });
  const settingsQuery = useCompanySettingsQuery();
  const companyProfileQuery = useCompanyProfileQuery();
  const tickets = ticketsQuery.data?.items ?? [];
  const loading = ticketsQuery.isLoading && !ticketsQuery.data;
  const error = ticketsQuery.error instanceof Error ? ticketsQuery.error.message : null;
  const isRefreshing =
    (ticketsQuery.isFetching && Boolean(ticketsQuery.data)) ||
    (allStatusTicketsQuery.isFetching && Boolean(allStatusTicketsQuery.data)) ||
    (settingsQuery.isFetching && Boolean(settingsQuery.data));
  const starterTicketLimitReached =
    user?.currentPlan === "STARTER" &&
    companyProfileQuery.data?.planEntitlements.tickets.limitReached === true;
  const starterTicketUsage = companyProfileQuery.data?.planEntitlements.tickets.used ?? 0;
  const starterTicketMax = companyProfileQuery.data?.planEntitlements.tickets.max ?? 5;

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (search.trim()) next.set("search", search.trim());
    else next.delete("search");
    if (status !== "ALL") next.set("status", status);
    else next.delete("status");
    if (type !== "ALL") next.set("type", type);
    else next.delete("type");
    setSearchParams(next, { replace: true });
  }, [search, status, type]);

  useEffect(() => {
    if (!settingsQuery.data) return;
    setAllowManualOverride(settingsQuery.data.allowManualApprovalOverride);
    setRequireRejectionComment(settingsQuery.data.requireRejectionComment);
  }, [settingsQuery.data]);

  useEffect(() => {
    if (!deleteTarget) {
      setDeleteImpact(null);
      setDeleteProjectWithTicket(false);
      setDeleteClientWithTicket(false);
      return;
    }

    let active = true;
    setDeleteImpactLoading(true);
    setDeleteImpact(null);
    setDeleteProjectWithTicket(false);
    setDeleteClientWithTicket(false);

    void apiRequest<{
      canDeleteProject: boolean;
      canDeleteClient: boolean;
      project: { id: string; name: string } | null;
      client: { id: string; name: string } | null;
    }>(`/tickets/${deleteTarget.id}/delete-impact`, { method: "GET" })
      .then((result) => {
        if (!active) return;
        setDeleteImpact(result);
      })
      .catch(() => {
        if (!active) return;
        setDeleteImpact(null);
      })
      .finally(() => {
        if (!active) return;
        setDeleteImpactLoading(false);
      });

    return () => {
      active = false;
    };
  }, [deleteTarget]);

  const counts = useMemo(() => {
    const source = allStatusTicketsQuery.data?.items ?? tickets;
    return {
      all: source.length,
      draft: source.filter((t) => t.status === "DRAFT").length,
      pending: source.filter((t) => t.status === "PENDING_APPROVAL").length,
      approved: source.filter((t) => t.status === "APPROVED").length,
      rejected: source.filter((t) => t.status === "REJECTED").length,
      clarification: source.filter((t) => t.status === "NEEDS_CLARIFICATION").length,
    };
  }, [allStatusTicketsQuery.data?.items, tickets]);

  const openTicket = useCallback(
    (ticket: TicketListItem) => {
      if (ticket.status === "DRAFT") {
        navigate(`/tickets/new?edit=${encodeURIComponent(ticket.id)}`);
        return;
      }
      navigate(`/tickets/${ticket.id}`);
    },
    [navigate],
  );

  const sortedTickets = useMemo(() => {
    if (sortBy === "project") {
      const groupedTickets = new Map<
        string,
        {
          projectId: string;
          projectName: string;
          projectCreatedAt: string;
          items: TicketListItem[];
        }
      >();

      for (const ticket of tickets) {
        const existingGroup = groupedTickets.get(ticket.project.id);
        if (existingGroup) {
          existingGroup.items.push(ticket);
          continue;
        }

        groupedTickets.set(ticket.project.id, {
          projectId: ticket.project.id,
          projectName: ticket.project.name,
          projectCreatedAt: ticket.project.createdAt,
          items: [ticket],
        });
      }

      const orderedGroups = [...groupedTickets.values()].sort((left, right) => {
        const projectDiff =
          new Date(left.projectCreatedAt).getTime() - new Date(right.projectCreatedAt).getTime();

        if (projectDiff !== 0) {
          return sortDirection === "asc" ? projectDiff : -projectDiff;
        }

        const nameDiff = left.projectName.localeCompare(right.projectName);
        if (nameDiff !== 0) {
          return sortDirection === "asc" ? nameDiff : -nameDiff;
        }

        return sortDirection === "asc"
          ? left.projectId.localeCompare(right.projectId)
          : right.projectId.localeCompare(left.projectId);
      });

      return orderedGroups.flatMap((group) =>
        [...group.items].sort((left, right) => {
          const createdDiff = new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime();
          if (createdDiff !== 0) {
            return sortDirection === "asc" ? createdDiff : -createdDiff;
          }

          return sortDirection === "asc"
            ? left.id.localeCompare(right.id)
            : right.id.localeCompare(left.id);
        }),
      );
    }

    const items = [...tickets];
    items.sort((left, right) => {
      if (sortBy === "createdAt") {
        const diff = new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime();
        return sortDirection === "asc" ? diff : -diff;
      }

      const statusDiff = STATUS_SORT_ORDER[left.status] - STATUS_SORT_ORDER[right.status];
      if (statusDiff !== 0) {
        return sortDirection === "asc" ? statusDiff : -statusDiff;
      }

      const createdDiff = new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime();
      return sortDirection === "asc" ? createdDiff : -createdDiff;
    });

    return items;
  }, [sortBy, sortDirection, tickets]);

  const toggleSort = (key: TicketSortKey) => {
    if (sortBy === key) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }

    setSortBy(key);
    setSortDirection(key === "createdAt" ? "desc" : key === "project" ? "desc" : "asc");
  };

  const renderSortIcon = (key: TicketSortKey) => {
    if (sortBy !== key) {
      return <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground/70" />;
    }

    return sortDirection === "asc" ? (
      <ChevronUp className="h-3.5 w-3.5 text-muted-foreground/80" />
    ) : (
      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground/80" />
    );
  };

  const handleDeleteTicket = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const query = new URLSearchParams();
      if (deleteProjectWithTicket) query.set("deleteProject", "1");
      if (deleteClientWithTicket) query.set("deleteClient", "1");
      await apiRequest(`/tickets/${deleteTarget.id}${query.toString() ? `?${query.toString()}` : ""}`, {
        method: "DELETE",
      });
      queryClient.setQueryData(currentTicketQueryKey, (current: { items?: TicketListItem[] } | undefined) =>
        current
          ? {
              ...current,
              items: (current.items ?? []).filter((item) => item.id !== deleteTarget.id),
            }
          : current,
      );
      void queryClient.invalidateQueries({ queryKey: ["tickets"] });
      if (deleteProjectWithTicket) {
        void queryClient.invalidateQueries({ queryKey: ["projects"] });
      }
      if (deleteClientWithTicket) {
        void queryClient.invalidateQueries({ queryKey: ["clients"] });
      }
      toast({ title: t("tickets.ticketDeleted") });
      setDeleteTarget(null);
      setDeleteImpact(null);
      setDeleteProjectWithTicket(false);
      setDeleteClientWithTicket(false);
    } catch (e) {
      toast({
        title: t("tickets.deleteFailed"),
        description: e instanceof Error ? e.message : t("tickets.unableToDeleteTicket"),
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const canShowManualActions = (ticket: TicketListItem) =>
    isAdmin &&
    allowManualOverride &&
    (ticket.status === "PENDING_APPROVAL" || ticket.status === "NEEDS_CLARIFICATION");

  const openManualAction = (ticket: TicketListItem, decision: "APPROVED" | "REJECTED") => {
    setManualAction({ ticket, decision });
    setManualReason("");
  };

  const canSendReminder = (ticket: TicketListItem) =>
    ticket.status === "PENDING_APPROVAL" && Boolean(ticket.approvalRequestId);

  const ticketReminderUsesSms = (ticket: TicketListItem) =>
    ticket.approvalDeliveryMethod === "SMS" ||
    ticket.approvalDeliveryMethod === "EMAIL_AND_SMS";

  const reminderLabel = (ticket: TicketListItem) =>
    t("tickets.sendReminderWithCount", { count: (ticket.reminderCount ?? 0) + 1 });

  const handleSendReminder = async (ticket: TicketListItem, confirmedSmsConsent = false) => {
    if (!ticket.approvalRequestId || sendingReminderTicketId === ticket.id) return;
    if (ticketReminderUsesSms(ticket) && !confirmedSmsConsent) {
      setSmsReminderTarget(ticket);
      setSmsReminderConsentError("");
      return;
    }

    setSendingReminderTicketId(ticket.id);
    const reminderToast = toast({
      title: t("tickets.sendingReminder"),
      description: ticket.ticketNumber,
    });
    try {
      await sendApprovalReminder(
        ticket.approvalRequestId,
        ticketReminderUsesSms(ticket)
          ? { smsConsentConfirmed: confirmedSmsConsent }
          : undefined,
      );
      reminderToast.update({
        id: reminderToast.id,
        title: t("ticketDetail.toasts.reminderQueued"),
        description: ticket.ticketNumber,
      });
      await invalidateTicketData(queryClient);
    } catch (e) {
      reminderToast.update({
        id: reminderToast.id,
        title: t("ticketDetail.toasts.reminderFailedTitle"),
        description: e instanceof Error ? e.message : t("common.requestFailed"),
        variant: "destructive",
      });
    } finally {
      setSendingReminderTicketId((current) => (current === ticket.id ? null : current));
      setSmsReminderTarget(null);
      setSmsReminderConsentConfirmed(false);
      setSmsReminderConsentError("");
    }
  };

  const handleManualDecision = async () => {
    if (!manualAction) return;

    if (
      manualAction.decision === "REJECTED" &&
      requireRejectionComment &&
      !manualReason.trim()
    ) {
      toast({
        title: t("tickets.manualReasonRequired"),
        description: t("tickets.manualReasonRequiredDescription"),
        variant: "destructive",
      });
      return;
    }

    setProcessingManualAction(true);
    try {
      const path =
        manualAction.decision === "APPROVED"
          ? `/tickets/${manualAction.ticket.id}/manual-approve`
          : `/tickets/${manualAction.ticket.id}/manual-reject`;

      await apiRequest(path, {
        method: "POST",
        body: {
          reason: manualReason.trim() || undefined,
        },
      });

      queryClient.setQueryData(currentTicketQueryKey, (current: { items?: TicketListItem[] } | undefined) => {
        if (!current) return current;
        const updated = (current.items ?? []).map((item) =>
          item.id === manualAction.ticket.id
            ? {
                ...item,
                status: manualAction.decision === "APPROVED" ? "APPROVED" : "REJECTED",
              }
            : item,
        );

        return {
          ...current,
          items: status === "ALL" ? updated : updated.filter((item) => item.status === status),
        };
      });
      void queryClient.invalidateQueries({ queryKey: ["tickets"] });

      toast({
        title:
          manualAction.decision === "APPROVED"
            ? t("tickets.ticketMarkedApproved")
            : t("tickets.ticketMarkedRejected"),
      });
      setManualAction(null);
      setManualReason("");
    } catch (e) {
      toast({
        title: t("tickets.manualActionFailed"),
        description: e instanceof Error ? e.message : t("tickets.unableToUpdateTicket"),
        variant: "destructive",
      });
    } finally {
      setProcessingManualAction(false);
    }
  };

  const smsReminderSending = Boolean(
    smsReminderTarget && sendingReminderTicketId === smsReminderTarget.id,
  );
  const smsReminderDeliveryLabel =
    smsReminderTarget?.approvalDeliveryMethod === "EMAIL_AND_SMS"
      ? t("tickets.emailAndSms")
      : t("tickets.sms");
  const smsReminderActionLabel =
    smsReminderTarget?.approvalDeliveryMethod === "EMAIL_AND_SMS"
      ? t("tickets.sendEmailAndSmsReminder")
      : t("tickets.sendSmsReminder");

  return (
    <div className="space-y-4 animate-fade-in">
      <PageHeader
        title={
          <div className="flex items-center gap-2">
            <span>{t("pages.tickets.title")}</span>
            <Popover open={ticketHelpOpen} onOpenChange={setTicketHelpOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-transparent text-muted-foreground transition-colors hover:bg-muted/30 hover:text-[#0C1F4F]"
                  aria-label={t("tickets.help.ariaLabel")}
                >
                  <Info className="h-4 w-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-[390px] border-border/80 bg-white p-4 shadow-lg"
              >
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-[#0C1F4F]">{t("tickets.help.title")}</p>
                    <p className="mt-1 text-sm leading-6 text-muted-foreground">{t("tickets.help.subtitle")}</p>
                  </div>
                  <ul className="space-y-2 text-sm leading-6 text-muted-foreground">
                    <li>{t("tickets.help.items.estimate")}</li>
                    <li>{t("tickets.help.items.changeOrder")}</li>
                    <li>{t("tickets.help.items.extraWork")}</li>
                    <li>{t("tickets.help.items.approvalFlow")}</li>
                    <li>{t("tickets.help.items.documentation")}</li>
                  </ul>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        }
        actions={
          <Button className="bg-accent text-accent-foreground hover:bg-accent/90 gap-1.5" onClick={() => navigate("/tickets/new")}>
            <Plus className="h-4 w-4" /> {t("tickets.newTicket")}
          </Button>
        }
      />

      {starterTicketLimitReached ? (
        <Alert className="border-amber-500/40 bg-amber-50 text-amber-950">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle>{t("dashboard.starterTicketLimitBanner.title")}</AlertTitle>
          <AlertDescription className="mt-2 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <span>
              {t("dashboard.starterTicketLimitBanner.description", {
                used: starterTicketUsage,
                max: starterTicketMax,
              })}
            </span>
            <Button
              type="button"
              size="sm"
              className="bg-accent text-accent-foreground hover:bg-accent/90"
              onClick={() => navigate("/upgrade-plan?targetPlan=pro&interval=MONTHLY")}
            >
              {t("dashboard.starterTicketLimitBanner.action")}
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="space-y-2 min-[970px]:space-y-0">
        <div className="flex flex-col gap-2 min-[970px]:flex-row min-[970px]:items-center min-[970px]:gap-3">
          <div className="flex items-center gap-3 min-[970px]:max-w-md min-[970px]:flex-1">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 pl-9 min-[970px]:h-9"
                placeholder={t("tickets.searchPlaceholder")}
              />
            </div>
            <RefreshingIndicator visible={isRefreshing} />
          </div>
          <div className="grid grid-cols-2 gap-2 min-[970px]:ml-auto min-[970px]:flex min-[970px]:items-center">
            <select
              className="h-9 rounded-md border bg-background px-3 text-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value as "ALL" | ApiTicketStatus)}
            >
              <option value="ALL">{t("tickets.all", { count: counts.all })}</option>
              <option value="DRAFT">{t("tickets.draft", { count: counts.draft })}</option>
              <option value="PENDING_APPROVAL">{t("tickets.pending", { count: counts.pending })}</option>
              <option value="APPROVED">{t("tickets.approved", { count: counts.approved })}</option>
              <option value="REJECTED">{t("tickets.rejected", { count: counts.rejected })}</option>
              <option value="NEEDS_CLARIFICATION">{t("tickets.clarification", { count: counts.clarification })}</option>
            </select>
            <select
              className="h-9 rounded-md border bg-background px-3 text-sm"
              value={type}
              onChange={(e) => setType(e.target.value as "ALL" | ApiTicketType)}
            >
              <option value="ALL">{t("tickets.allTypes")}</option>
              <option value="TM_TICKET">{t("ticketTypes.estimate")}</option>
              <option value="CHANGE_ORDER">{t("ticketTypes.changeOrder")}</option>
              <option value="EXTRA_WORK">{t("ticketTypes.extraWork")}</option>
            </select>
            <Button variant="outline" className="hidden min-[970px]:inline-flex gap-1.5" onClick={() => void ticketsQuery.refetch()}>
              <RefreshCw className="h-3.5 w-3.5" /> {t("tickets.refresh")}
            </Button>
          </div>
          {(searchParams.get("projectId") || searchParams.get("startDate")) ? (
            <span className="text-xs text-muted-foreground">{t("tickets.contextFiltersApplied")}</span>
          ) : null}
        </div>
      </div>

      {error ? (
        <StateCard
          icon={AlertTriangle}
          tone="error"
          title={t("tickets.failedToLoad")}
          description={error}
          primaryActionLabel={t("common.retry")}
          onPrimaryAction={() => void ticketsQuery.refetch()}
        />
      ) : loading ? (
        <TableSkeleton columns={8} rows={6} />
      ) : sortedTickets.length === 0 ? (
        <StateCard
          icon={FileX}
          title={t("tickets.noTicketsFound")}
          description={t("tickets.noTicketsDescription")}
        />
      ) : (
        <>
          <div className="min-[767px]:hidden space-y-3">
            {sortedTickets.map((ticket) => (
              <CompactTicketCard
                key={`mobile-${ticket.id}`}
                ticket={ticket}
                onOpen={() => openTicket(ticket)}
                actions={
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="flex h-full min-h-[56px] w-12 shrink-0 items-center justify-center rounded-md hover:bg-secondary/60"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/tickets/new?edit=${ticket.id}`);
                        }}
                      >
                        <Pencil className="h-4 w-4 mr-2" /> {t("tickets.edit")}
                      </DropdownMenuItem>
                      {canSendReminder(ticket) ? (
                        <DropdownMenuItem
                          disabled={sendingReminderTicketId === ticket.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleSendReminder(ticket);
                          }}
                        >
                          <Bell className="h-4 w-4 mr-2" />
                          {sendingReminderTicketId === ticket.id ? t("common.sending") : reminderLabel(ticket)}
                        </DropdownMenuItem>
                      ) : null}
                      {canShowManualActions(ticket) ? (
                        <>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              openManualAction(ticket, "APPROVED");
                            }}
                          >
                            {t("tickets.markApprovedAdmin")}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              openManualAction(ticket, "REJECTED");
                            }}
                          >
                            {t("tickets.markRejectedAdmin")}
                          </DropdownMenuItem>
                        </>
                      ) : null}
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(ticket);
                        }}
                      >
                        <Trash2 className="h-4 w-4 mr-2" /> {t("tickets.delete")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                }
              />
            ))}
          </div>

          <div className="hidden min-[767px]:block min-[1140px]:hidden space-y-3">
            {sortedTickets.map((ticket) => (
              <CompactTicketCard
                key={`tablet-${ticket.id}`}
                ticket={ticket}
                onOpen={() => openTicket(ticket)}
                actions={
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="flex h-full min-h-[56px] w-12 items-center justify-center rounded-md hover:bg-secondary/60"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenuItem onClick={() => navigate(`/tickets/new?edit=${ticket.id}`)}>
                        <Pencil className="h-4 w-4 mr-2" /> {t("tickets.edit")}
                      </DropdownMenuItem>
                      {canSendReminder(ticket) ? (
                        <DropdownMenuItem
                          disabled={sendingReminderTicketId === ticket.id}
                          onClick={() => void handleSendReminder(ticket)}
                        >
                          <Bell className="h-4 w-4 mr-2" />
                          {sendingReminderTicketId === ticket.id ? t("common.sending") : reminderLabel(ticket)}
                        </DropdownMenuItem>
                      ) : null}
                      {canShowManualActions(ticket) ? (
                        <>
                          <DropdownMenuItem onClick={() => openManualAction(ticket, "APPROVED")}>
                            {t("tickets.markApprovedAdmin")}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openManualAction(ticket, "REJECTED")}>
                            {t("tickets.markRejectedAdmin")}
                          </DropdownMenuItem>
                        </>
                      ) : null}
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={() => setDeleteTarget(ticket)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" /> {t("tickets.delete")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                }
              />
            ))}
          </div>

          <div className="hidden min-[1140px]:block rounded-lg border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="bg-muted/35 px-4 py-3 text-left text-[#0C1F4F]">{t("tickets.ticketNumber")}</th>
                  <th className="border-l border-border/60 px-4 py-3 text-left text-[#0C1F4F]">{t("tickets.type")}</th>
                  <th className="border-l border-border/60 bg-muted/35 px-4 py-3 text-left text-[#0C1F4F]">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 transition-colors hover:text-[#0C1F4F]/75"
                      onClick={() => toggleSort("project")}
                    >
                      <span>{t("tickets.project")}</span>
                      {renderSortIcon("project")}
                    </button>
                  </th>
                  <th className="border-l border-border/60 px-4 py-3 text-left text-[#0C1F4F]">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 transition-colors hover:text-[#0C1F4F]/75"
                      onClick={() => toggleSort("createdAt")}
                    >
                      <span>{t("tickets.createdAt")}</span>
                      {renderSortIcon("createdAt")}
                    </button>
                  </th>
                  <th className="border-l border-border/60 bg-muted/35 px-4 py-3 text-center text-[#0C1F4F]">{t("tickets.opened")}</th>
                  <th className="border-l border-border/60 px-4 py-3 text-right text-[#0C1F4F]">{t("tickets.value")}</th>
                  <th className="border-l border-border/60 bg-muted/35 px-4 py-3 text-center text-[#0C1F4F]">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 transition-colors hover:text-[#0C1F4F]/75"
                      onClick={() => toggleSort("status")}
                    >
                      <span>{t("tickets.status")}</span>
                      {renderSortIcon("status")}
                    </button>
                  </th>
                  <th className="w-[50px] border-l border-border/60 px-0 py-3 text-center text-[#0C1F4F]"></th>
                </tr>
              </thead>
              <tbody>
                {sortedTickets.map((ticket) => (
                  <tr
                    key={ticket.id}
                    className="border-b last:border-0 hover:bg-muted/30 cursor-pointer"
                    onClick={() => openTicket(ticket)}
                  >
                    <td className="bg-muted/35 px-4 py-3 text-sm font-normal text-muted-foreground">{ticket.ticketNumber}</td>
                    <td className="border-l border-border/50 px-4 py-3 font-semibold text-accent">
                      {ticket.type === "TM_TICKET" ? t("ticketTypes.estimate") : ticket.type === "CHANGE_ORDER" ? t("ticketTypes.changeOrder") : t("ticketTypes.extraWork")}
                    </td>
                    <td className="border-l border-border/50 bg-muted/35 px-4 py-3">
                      <p className="font-medium text-[#0C1F4F]">{ticket.project.name}</p>
                      {hasTeamAssignment ? (
                        assignedMemberLabel(ticket) ? (
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            Assigned to: {assignedMemberLabel(ticket)}
                          </p>
                        ) : (
                          <span className="mt-1 inline-flex rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                            Unassigned
                          </span>
                        )
                      ) : null}
                    </td>
                    <td className="border-l border-border/50 px-4 py-3 text-muted-foreground">{formatTicketCreatedAt(ticket.createdAt)}</td>
                    <td className="border-l border-border/50 bg-muted/35 px-4 py-3 text-center text-muted-foreground">
                      {renderOpenedIndicator(ticket, t)}
                    </td>
                    <td className="border-l border-border/50 px-4 py-3 text-right font-medium">{formatCurrency(ticket.totalAmount)}</td>
                    <td className="border-l border-border/50 bg-muted/35 px-4 py-3 text-center">
                      <div className="flex justify-center">
                        <StatusChip status={mapApiStatusToUi(ticket.status)} />
                      </div>
                    </td>
                    <td
                      className="w-[50px] border-l border-border/50 p-0 text-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="flex h-full min-h-[68px] w-full items-center justify-center rounded-none hover:bg-secondary/60"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/tickets/new?edit=${ticket.id}`);
                            }}
                        >
                          <Pencil className="h-4 w-4 mr-2" /> {t("tickets.edit")}
                        </DropdownMenuItem>
                          {canSendReminder(ticket) ? (
                            <DropdownMenuItem
                              disabled={sendingReminderTicketId === ticket.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleSendReminder(ticket);
                              }}
                            >
                              <Bell className="h-4 w-4 mr-2" />
                              {sendingReminderTicketId === ticket.id ? t("common.sending") : reminderLabel(ticket)}
                            </DropdownMenuItem>
                          ) : null}
                          {canShowManualActions(ticket) ? (
                            <>
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openManualAction(ticket, "APPROVED");
                                }}
                              >
                                {t("tickets.markApprovedAdmin")}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openManualAction(ticket, "REJECTED");
                                }}
                              >
                                {t("tickets.markRejectedAdmin")}
                              </DropdownMenuItem>
                            </>
                          ) : null}
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget(ticket);
                            }}
                          >
                            <Trash2 className="h-4 w-4 mr-2" /> {t("tickets.delete")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <AlertDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteTarget(null);
            setDeleteImpact(null);
            setDeleteProjectWithTicket(false);
            setDeleteClientWithTicket(false);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("tickets.deleteDialogTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("tickets.deleteDialogDescription", { ticketNumber: deleteTarget?.ticketNumber ?? "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            {deleteImpactLoading ? (
              <p className="text-sm text-muted-foreground">{t("tickets.checkingRelated")}</p>
            ) : null}
            {deleteImpact?.canDeleteProject && deleteImpact.project ? (
              <label className="flex items-start gap-3 rounded-md border border-border/70 p-3">
                <Checkbox
                  checked={deleteProjectWithTicket}
                  onCheckedChange={(checked) => {
                    const next = Boolean(checked);
                    setDeleteProjectWithTicket(next);
                    if (!next) setDeleteClientWithTicket(false);
                  }}
                />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-[#0C1F4F]">
                    {t("tickets.alsoDeleteProject", { name: deleteImpact.project.name })}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t("tickets.projectOnlyHasThisTicket")}
                  </p>
                </div>
              </label>
            ) : null}
            {deleteProjectWithTicket && deleteImpact?.canDeleteClient && deleteImpact.client ? (
              <label className="flex items-start gap-3 rounded-md border border-border/70 p-3">
                <Checkbox
                  checked={deleteClientWithTicket}
                  onCheckedChange={(checked) => setDeleteClientWithTicket(Boolean(checked))}
                />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-[#0C1F4F]">
                    {t("tickets.alsoDeleteClient", { name: deleteImpact.client.name })}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t("tickets.clientOnlyHasThisProject")}
                  </p>
                </div>
              </label>
            ) : null}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleDeleteTicket();
              }}
              disabled={deleting || deleteImpactLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? t("common.deleting") : t("tickets.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={Boolean(manualAction)}
        onOpenChange={(open) => {
          if (!open && !processingManualAction) {
            setManualAction(null);
            setManualReason("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {manualAction?.decision === "APPROVED"
                ? t("tickets.markApprovedTitle")
                : t("tickets.markRejectedTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {manualAction
                ? t("tickets.manualActionDescription", { ticketNumber: manualAction.ticket.ticketNumber })
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="manual-ticket-reason">
              {manualAction?.decision === "APPROVED"
                ? t("tickets.internalNoteOptional")
                : t("tickets.rejectionReason")}
            </Label>
            <Textarea
              id="manual-ticket-reason"
              value={manualReason}
              onChange={(e) => setManualReason(e.target.value)}
              placeholder={
                manualAction?.decision === "APPROVED"
                  ? t("tickets.approvalTimelineNotePlaceholder")
                  : t("tickets.rejectionReasonPlaceholder")
              }
            />
            {manualAction?.decision === "REJECTED" && requireRejectionComment ? (
              <p className="text-xs text-muted-foreground">
                {t("tickets.rejectionCommentRequired")}
              </p>
            ) : null}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processingManualAction}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleManualDecision();
              }}
              disabled={processingManualAction}
              className={
                manualAction?.decision === "REJECTED"
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  : undefined
              }
            >
              {processingManualAction
                ? t("tickets.updating")
                : manualAction?.decision === "APPROVED"
                  ? t("tickets.markApproved")
                  : t("tickets.markRejected")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={Boolean(smsReminderTarget)}
        onOpenChange={(open) => {
          if (smsReminderSending) return;
          if (!open) {
            setSmsReminderTarget(null);
            setSmsReminderConsentConfirmed(false);
            setSmsReminderConsentError("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("tickets.confirmSmsConsentTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("tickets.confirmSmsConsentDescription", {
                method: smsReminderDeliveryLabel,
                ticketNumber: smsReminderTarget?.ticketNumber,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <SmsConsentConfirmation
            checked={smsReminderConsentConfirmed}
            onCheckedChange={(checked) => {
              setSmsReminderConsentConfirmed(checked);
              if (checked) setSmsReminderConsentError("");
            }}
            error={smsReminderConsentError}
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={smsReminderSending}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={smsReminderSending}
              onClick={(event) => {
                event.preventDefault();
                if (!smsReminderTarget || smsReminderSending) return;
                if (!smsReminderConsentConfirmed) {
                  setSmsReminderConsentError(SMS_TRANSACTIONAL_CONSENT_REQUIRED_MESSAGE);
                  return;
                }
                void handleSendReminder(smsReminderTarget, true);
              }}
              className="gap-1.5"
            >
              {smsReminderSending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t("common.sending")}
                </>
              ) : (
                smsReminderActionLabel
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
